import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, User, Menu, X, MessageCircle, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { whatsappLink } from "@/lib/api";
import SearchOverlay from "@/components/SearchOverlay";

const MEGA = [
    {
        title: "BY CATEGORY",
        items: [
            { name: "Gold Jewellery", path: "/gold-jewellery" },
            { name: "Silver Jewellery", path: "/silver-jewellery" },
            { name: "Diamond Jewellery", path: "/diamond-jewellery" },
            { name: "Bridal Jewellery", path: "/bridal-jewellery" },
        ],
    },
    {
        title: "BY TYPE",
        items: [
            { name: "Rings", path: "/products?subcategory=rings" },
            { name: "Earrings", path: "/products?subcategory=earrings" },
            { name: "Necklaces", path: "/products?subcategory=necklaces" },
            { name: "Bangles", path: "/products?subcategory=bangles" },
            { name: "Chains", path: "/products?subcategory=chains" },
            { name: "Pendants", path: "/products?subcategory=pendants" },
            { name: "Mangalsutra", path: "/products?subcategory=mangalsutra" },
        ],
    },
    {
        title: "BY OCCASION",
        items: [
            { name: "Wedding", path: "/bridal-jewellery" },
            { name: "Festive", path: "/products?collection=Traditional" },
            { name: "Daily Wear", path: "/products?collection=Everyday" },
            { name: "Gifting", path: "/products?collection=Contemporary" },
        ],
    },
    {
        title: "BY COLLECTION",
        items: [
            { name: "New Arrivals", path: "/new-arrivals" },
            { name: "Trending", path: "/products?trending=true" },
            { name: "Featured", path: "/products?featured=true" },
            { name: "Traditional", path: "/products?collection=Traditional" },
            { name: "Contemporary", path: "/products?collection=Contemporary" },
        ],
    },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [openMega, setOpenMega] = useState(false);
    const [openMobile, setOpenMobile] = useState(false);
    const [openSearch, setOpenSearch] = useState(false);
    const [search, setSearch] = useState("");
    const { user, logout } = useAuth();
    const { settings } = useSettings();
    const nav = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const doSearch = (e) => {
        e.preventDefault();
        if (search.trim()) nav(`/products?search=${encodeURIComponent(search)}`);
    };

    const navLinks = [
        { name: "HOME", path: "/" },
        { name: "JEWELLERY", path: "/products", mega: true },
        { name: "GOLD", path: "/gold-jewellery" },
        { name: "SILVER", path: "/silver-jewellery" },
        { name: "DIAMOND", path: "/diamond-jewellery" },
        { name: "BRIDAL", path: "/bridal-jewellery" },
        { name: "GOLD HARVEST", path: "/gold-harvest" },
        { name: "KITTY PLAN", path: "/kitty-plan" },
        { name: "ABOUT", path: "/about" },
        { name: "CONTACT", path: "/contact" },
    ];

    return (
        <header className={`sticky top-0 z-40 bg-[color:var(--ivory)] transition-all duration-300 ${scrolled ? "header-scrolled py-2" : "py-4"}`} data-testid="site-header">
            <div className="max-w-[1440px] mx-auto px-5 lg:px-10 flex items-center justify-between gap-6">
                <button className="lg:hidden text-[color:var(--charcoal)]" onClick={() => setOpenMobile(true)} data-testid="mobile-menu-btn" aria-label="Menu">
                    <Menu size={22} />
                </button>

                <Link to="/" className="flex-shrink-0 flex items-center gap-2" data-testid="brand-logo">
                    <img src="/logo.png" alt="Singh Jewellers" className={`transition-all ${scrolled ? "h-10" : "h-12 lg:h-14"} w-auto`} draggable="false" />
                    <span className="hidden sm:inline tracking-[0.32em] font-accent text-[color:var(--charcoal)] text-sm lg:text-base">SINGH<span className="text-[color:var(--champagne)] mx-1">·</span>JEWELLERS</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-6 text-[11px] font-sans-body tracking-[0.18em] font-medium text-[color:var(--charcoal)]">
                    {navLinks.map((l) => (
                        <div key={l.name}
                            onMouseEnter={() => l.mega && setOpenMega(true)}
                            onMouseLeave={() => l.mega && setOpenMega(false)}
                        >
                            <Link to={l.path} className="link-underline hover:text-[color:var(--champagne)] transition-colors" data-testid={`nav-${l.name.toLowerCase().replace(/\s/g, "-")}`}>
                                {l.name}
                            </Link>
                        </div>
                    ))}
                </nav>

                <div className="flex items-center gap-3 lg:gap-5">
                    <button onClick={() => setOpenSearch(true)} className="text-[color:var(--charcoal)] hover:text-[color:var(--champagne)]" data-testid="search-toggle" aria-label="Search">
                        <Search size={18} />
                    </button>
                    <Link to="/wishlist" data-testid="nav-wishlist" className="text-[color:var(--charcoal)] hover:text-[color:var(--champagne)]">
                        <Heart size={18} />
                    </Link>
                    <Link to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"} data-testid="nav-account" className="text-[color:var(--charcoal)] hover:text-[color:var(--champagne)]">
                        <User size={18} />
                    </Link>
                    <a href={whatsappLink(settings.whatsapp_number, "Hello Singh Jewellers, I would like to make an enquiry.")} target="_blank" rel="noreferrer" className="hidden lg:flex text-[color:var(--whatsapp)]" data-testid="nav-whatsapp">
                        <MessageCircle size={18} />
                    </a>
                    {user && (
                        <button onClick={logout} data-testid="nav-logout" className="hidden lg:flex text-[color:var(--muted-brown)] hover:text-[color:var(--charcoal)]" title="Logout">
                            <LogOut size={16} />
                        </button>
                    )}
                </div>
            </div>

            {/* Mega Menu */}
            {openMega && (
                <div
                    onMouseEnter={() => setOpenMega(true)}
                    onMouseLeave={() => setOpenMega(false)}
                    className="hidden lg:block absolute left-0 right-0 top-full bg-[color:var(--pearl)] border-t border-[color:var(--soft-border)] shadow-xl"
                    data-testid="mega-menu"
                >
                    <div className="max-w-[1440px] mx-auto grid grid-cols-5 gap-10 px-10 py-10">
                        {MEGA.map((col) => (
                            <div key={col.title}>
                                <div className="eyebrow mb-4">{col.title}</div>
                                <ul className="space-y-2.5">
                                    {col.items.map((it) => (
                                        <li key={it.name}>
                                            <Link to={it.path} className="text-sm text-[color:var(--charcoal)] hover:text-[color:var(--champagne)] transition-colors">{it.name}</Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                        <div className="relative">
                            <img src="https://images.unsplash.com/photo-1617633150878-7df1d12a9a57?w=600" alt="Bridal" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-[color:var(--charcoal)]/30 flex flex-col items-start justify-end p-5">
                                <div className="eyebrow text-white mb-1">Featured</div>
                                <div className="text-white font-serif-luxe text-xl leading-tight mb-3">Bridal Heritage</div>
                                <Link to="/bridal-jewellery" className="text-[11px] tracking-[0.22em] text-white border-b border-[color:var(--champagne)] pb-0.5">EXPLORE →</Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile drawer */}
            {openMobile && (
                <div className="lg:hidden fixed inset-0 z-50 bg-[color:var(--ivory)] overflow-y-auto" data-testid="mobile-drawer">
                    <div className="p-5 flex items-center justify-between border-b border-[color:var(--soft-border)]">
                        <span className="font-accent tracking-[0.3em] text-sm">SINGH · JEWELLERS</span>
                        <button onClick={() => setOpenMobile(false)} data-testid="mobile-close"><X size={22} /></button>
                    </div>
                    <div className="p-6 space-y-1">
                        {navLinks.map((l) => (
                            <Link key={l.name} to={l.path} onClick={() => setOpenMobile(false)}
                                className="block py-3 text-sm font-sans-body tracking-[0.18em] border-b border-[color:var(--soft-border)] text-[color:var(--charcoal)]">
                                {l.name}
                            </Link>
                        ))}
                        <div className="pt-6 flex gap-3">
                            {user ? (
                                <button className="btn-outline flex-1" onClick={() => { logout(); setOpenMobile(false); }}>LOGOUT</button>
                            ) : (
                                <Link className="btn-primary flex-1" to="/login" onClick={() => setOpenMobile(false)}>LOGIN</Link>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <SearchOverlay open={openSearch} onClose={() => setOpenSearch(false)} />
        </header>
    );
}
